import React, { useState, useMemo, memo, useEffect, useRef } from 'react';
import { useTodos, Todo, ReminderTiming, SubTodo } from '@/hooks/useTodos';
import { getSettings } from '@/utils/storage';
import { Settings as AppSettings } from '@/types';
import TodoHeatmap from '@/components/TodoHeatmap';
import DatePicker from '@/components/DatePicker';
import ReminderPicker from '@/components/ReminderPicker';
import PriorityPicker from '@/components/PriorityPicker';
import { Plus, Calendar, AlertCircle, CheckCircle2, Circle, Trash2, Clock, Settings, Bell, ChevronDown, ChevronRight, CornerDownRight, X, Timer, ListTodo, Edit2, Flag, Activity, CalendarOff } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';

// --- Components ---

// 1. AddTodoForm
const AddTodoForm = memo(({ onAdd, initialDate }: { onAdd: (text: string, date: Date | null, reminder: ReminderTiming, subTodos: string[], priority?: 'high' | 'medium' | 'low') => void, initialDate?: Date | null }) => {
    const [newTodo, setNewTodo] = useState('');
    const [dueDate, setDueDate] = useState<Date | null>(() => {
        if (initialDate) return initialDate;
        // Default logic: 
        // If current time, +4 hours.
        // But if user picks a date from calendar later, we need to handle that in the DatePicker change or here?
        // Wait, this is initial state.
        const now = new Date();
        const d = new Date(now);
        d.setHours(d.getHours() + 4);
        return d;
    });
    const [priority, setPriority] = useState<'high' | 'medium' | 'low' | undefined>(undefined);
    
    const handleDateChange = (date: Date | null) => {
        if (!date) {
            setDueDate(null);
            return;
        }
        
        // If date is the same, user is likely editing time, so trust the input
        if (dueDate && date.toDateString() === dueDate.toDateString()) {
            setDueDate(date);
            return;
        }
        
        const now = new Date();
        const isToday = date.toDateString() === now.toDateString();
        
        const newDate = new Date(date);
        
        if (isToday) {
            // If today, set to current time + 4 hours
            // But we need to keep the date part same as 'date' (which is today)
            // Actually 'date' from DatePicker might have 00:00:00 time if it's just date selection?
            // Let's assume DatePicker returns date with some time. 
            // If it's the "Quick Add Date" passed in, it might be 00:00.
            
            // Re-calculate +4h from NOW
            const future = new Date();
            future.setHours(future.getHours() + 4);
            newDate.setHours(future.getHours(), future.getMinutes(), 0, 0);
        } else {
            // If other day, set to 12:00
            newDate.setHours(12, 0, 0, 0);
        }
        setDueDate(newDate);
    };

    // Sync initialDate when it changes (e.g. from calendar click)
    useEffect(() => {
        if (initialDate) {
            // Use the custom logic for initialDate too? 
            // Usually initialDate comes from "Quick Add" which is just a date click (00:00 usually).
            handleDateChange(initialDate);
        }
    }, [initialDate]);

    const [reminder, setReminder] = useState<ReminderTiming>(20);
    
    // Sub-tasks draft state
    const [isDraftMode, setIsDraftMode] = useState(false);
    const [subTodos, setSubTodos] = useState<string[]>([]);
    const [newSubTodoInput, setNewSubTodoInput] = useState('');
    const subInputRef = React.useRef<HTMLInputElement>(null);

    // Dynamic Placeholder Logic
    const [placeholderText, setPlaceholderText] = useState('');
    const [isFadingOut, setIsFadingOut] = useState(false);
    const [phraseIndex, setPhraseIndex] = useState(0);
    const [charIndex, setCharIndex] = useState(0);

    const phrases = useMemo(() => [
        "输入新任务，完成每一次升级...",
        "迎接新挑战，待办未来之待办...",
        "每一次出发都是新的开始..."
    ], []);
    
    useEffect(() => {
        const currentPhrase = phrases[phraseIndex] + "（Enter键入/Tab子任务）";
        
        // Typing Phase
        if (!isFadingOut && charIndex < currentPhrase.length) {
            const timeout = setTimeout(() => {
                setPlaceholderText(currentPhrase.slice(0, charIndex + 1));
                setCharIndex(prev => prev + 1);
            }, 30); // Faster typing (30ms)
            return () => clearTimeout(timeout);
        }
        
        // Waiting Phase (Full text displayed)
        if (!isFadingOut && charIndex === currentPhrase.length) {
            const timeout = setTimeout(() => {
                setIsFadingOut(true);
            }, 2000); // Stay for 2s
            return () => clearTimeout(timeout);
        }

        // Fading Out Phase
        if (isFadingOut) {
             const timeout = setTimeout(() => {
                 setPhraseIndex((prev) => (prev + 1) % phrases.length);
                 setCharIndex(0);
                 setPlaceholderText('');
                 setIsFadingOut(false);
             }, 500); // Match CSS transition duration
             return () => clearTimeout(timeout);
        }
    }, [charIndex, isFadingOut, phraseIndex, phrases]);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (!newTodo.trim()) return;
        
        let finalSubTodos = [...subTodos];
        if (newSubTodoInput.trim()) {
            finalSubTodos.push(newSubTodoInput.trim());
        }

        onAdd(newTodo, dueDate, reminder, finalSubTodos, priority);
        
        setNewTodo('');
        // Reset to next 4 hours
        const d = new Date();
        d.setHours(d.getHours() + 4);
        setDueDate(d);
        setReminder(20);
        setPriority(undefined);
        setSubTodos([]);
        setNewSubTodoInput('');
        setIsDraftMode(false);
    };

    const handleAddSubTodoDraft = (e: React.FormEvent) => {
        e.preventDefault();
        if (newSubTodoInput.trim() || newSubTodoInput === '') {
            if (newSubTodoInput.trim()) {
                setSubTodos([...subTodos, newSubTodoInput.trim()]);
                setNewSubTodoInput('');
                // Keep focus
                setTimeout(() => subInputRef.current?.focus(), 0);
            }
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && e.ctrlKey) {
            e.preventDefault();
            setIsDraftMode(true);
            setTimeout(() => subInputRef.current?.focus(), 100);
        }
        if (e.key === 'Tab' && !e.shiftKey && newTodo.trim()) {
             e.preventDefault();
             setIsDraftMode(true);
             setTimeout(() => subInputRef.current?.focus(), 100);
        }
        // Esc to save/submit if there is content
        if (e.key === 'Escape') {
            e.preventDefault();
            if (newTodo.trim()) {
                handleSubmit(e);
            }
        }
    };

    const reminderOptions: { label: string, value: ReminderTiming }[] = [
        { label: '不提醒', value: null },
        { label: '提前10分钟', value: 10 },
        { label: '提前20分钟', value: 20 },
        { label: '提前30分钟', value: 30 },
        { label: '提前1小时', value: '1h' },
        { label: '提前12小时', value: '12h' },
        { label: '提前1天', value: '1d' },
    ];

    const priorityOptions: { label: string, value: 'high' | 'medium' | 'low', color: string }[] = [
        { label: '高优先级', value: 'high', color: 'text-red-500' },
        { label: '中优先级', value: 'medium', color: 'text-blue-500' },
        { label: '低优先级', value: 'low', color: 'text-green-500' },
    ];

    return (
        <div className="bg-white dark:bg-slate-800 p-3 sm:p-4 rounded-xl sm:rounded-2xl shadow-sm border border-slate-200 dark:border-white/5 mb-0 lg:mb-8 transition-all relative z-30">
            <form onSubmit={handleSubmit} className="flex flex-col gap-2 sm:gap-3">
                <div className="flex gap-2">
                    <div className="relative flex-1">
                        {!newTodo && (
                            <span className={`absolute left-0 top-1/2 -translate-y-1/2 text-base text-slate-400 pointer-events-none transition-opacity duration-500 ${isFadingOut ? 'opacity-0' : 'opacity-100'}`}>
                                {placeholderText}
                            </span>
                        )}
                        <input
                            type="text"
                            value={newTodo}
                            onChange={(e) => setNewTodo(e.target.value)}
                            className="w-full bg-transparent text-lg outline-none text-slate-800 dark:text-white placeholder:text-slate-400"
                            onFocus={() => {
                                // setIsDraftMode(true) // User requested NOT to show default subtask input
                            }}
                            onKeyDown={handleKeyDown}
                        />
                    </div>
                    {isDraftMode && (
                        <button 
                            type="button"
                            onClick={() => {
                                setIsDraftMode(false);
                                setNewTodo('');
                                setSubTodos([]);
                            }}
                            className="text-slate-400 hover:text-slate-600"
                        >
                            <X size={20} />
                        </button>
                    )}
                </div>

                <AnimatePresence>
                    {isDraftMode && (
                        <motion.div 
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            className="space-y-2"
                        >
                            {subTodos.map((st, idx) => (
                                <div key={idx} className="flex items-center gap-2 pl-4 text-sm text-slate-600 dark:text-slate-300 group/draft">
                                    <CornerDownRight size={14} className="text-slate-300" />
                                    <input 
                                        type="text"
                                        value={st}
                                        onChange={(e) => {
                                            const newSubTodos = [...subTodos];
                                            newSubTodos[idx] = e.target.value;
                                            setSubTodos(newSubTodos);
                                        }}
                                        className="flex-1 bg-transparent outline-none border-b border-transparent focus:border-blue-500 px-1"
                                    />
                                    <button 
                                        type="button" 
                                        onClick={() => setSubTodos(subTodos.filter((_, i) => i !== idx))}
                                        className="ml-auto text-slate-400 hover:text-red-500 opacity-0 group-hover/draft:opacity-100 transition-opacity"
                                    >
                                        <X size={14} />
                                    </button>
                                </div>
                            ))}
                            
                            <div className="flex items-center gap-2 pl-4">
                                <CornerDownRight size={14} className="text-slate-300" />
                                <input 
                                    ref={subInputRef}
                                    type="text"
                                    value={newSubTodoInput}
                                    onChange={(e) => setNewSubTodoInput(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            if (e.ctrlKey) {
                                                // Ctrl+Enter: Add current subtask (if any) AND Submit Main Task
                                                e.preventDefault();
                                                handleSubmit(e);
                                            } else {
                                                // Enter: Add subtask if has text, OR Submit Main Task if empty
                                                // User wants continuous entry: Add subtask, clear input, keep focus
                                                if (newSubTodoInput.trim()) {
                                                    e.preventDefault(); // Prevent form submit
                                                    handleAddSubTodoDraft(e);
                                                } else {
                                                    // If empty, user might want to submit the whole todo
                                                    e.preventDefault();
                                                    handleSubmit(e);
                                                }
                                            }
                                        }
                                        if (e.key === 'Backspace' && newSubTodoInput === '') {
                                            // Allow exiting sub-task mode if empty
                                            if (subTodos.length === 0) {
                                                setIsDraftMode(false);
                                            }
                                        }
                                        if (e.key === 'Escape') {
                                            // Save draft (if any) and exit subtask mode? 
                                            // Or just exit? User said "Save and exit".
                                            // Usually Escape means Cancel.
                                            // But for "Save and Exit", maybe they mean the Whole Todo?
                                            // "Esc 保存退出编辑" usually applies to editing an existing item.
                                            // For creation, let's make Esc close draft mode if empty, or just focus back to main input?
                                            e.preventDefault();
                                            setIsDraftMode(false);
                                            // If there was text, maybe add it?
                                            if (newSubTodoInput.trim()) {
                                                setSubTodos([...subTodos, newSubTodoInput.trim()]);
                                                setNewSubTodoInput('');
                                            }
                                        }
                                    }}
                                    placeholder="输入子任务..."
                                    className="flex-1 text-sm bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded px-2 py-1 outline-none text-slate-700 dark:text-slate-300"
                                />
                                <button 
                                    type="button"
                                    onClick={() => {
                                        setNewSubTodoInput('');
                                        // Focus back
                                        setTimeout(() => subInputRef.current?.focus(), 0);
                                    }}
                                    className="p-1 text-slate-400 hover:text-red-500 rounded"
                                    title="清空输入"
                                >
                                    <X size={16} />
                                </button>
                                <button 
                                    type="button"
                                    onClick={() => {
                                        setIsDraftMode(false);
                                        setNewSubTodoInput('');
                                    }}
                                    className="p-1 text-slate-400 hover:text-slate-600 rounded ml-1 bg-slate-100 dark:bg-slate-700"
                                    title="退出子任务模式"
                                >
                                    <ChevronDown size={16} />
                                </button>
                                <button 
                                    type="button"
                                    onClick={handleAddSubTodoDraft}
                                    disabled={!newSubTodoInput.trim()}
                                    className="p-1 text-blue-500 hover:bg-blue-50 rounded disabled:opacity-50"
                                >
                                    <Plus size={16} />
                                </button>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>

                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between border-t border-slate-100 dark:border-white/5 pt-3 mt-1 gap-3 sm:gap-0">
                    <div className="flex flex-wrap items-center gap-2 sm:gap-3 w-full sm:w-auto">
                        <DatePicker 
                            value={dueDate} 
                            onChange={handleDateChange} 
                            placeholder="设置日期"
                        />
                        
                        <ReminderPicker 
                            value={reminder} 
                            onChange={setReminder}
                            className="relative"
                        >
                            <button
                                type="button"
                                className={`flex items-center gap-1 text-xs px-2 py-1.5 rounded-lg border transition-colors ${
                                    reminder 
                                    ? 'bg-blue-50 text-blue-600 border-blue-200 dark:bg-blue-900/30 dark:border-blue-800' 
                                    : 'bg-white text-slate-500 border-slate-200 hover:border-slate-300 dark:bg-slate-800 dark:border-slate-700 dark:text-slate-400'
                                }`}
                                title="设置提醒"
                            >
                                <svg viewBox="0 0 24 24" width="12" height="12" className={reminder ? 'fill-current' : 'fill-none stroke-current stroke-2'}>
                                    <path d="M11.9998 2.3999C7.88636 2.3999 4.60947 5.79512 4.79336 9.93291L4.82137 10.4571C4.85177 11.0264 4.71717 11.5891 4.43745 12.0886L3.58572 13.6095C2.95555 14.7348 3.76936 16.1264 5.06019 16.1264H18.9395C20.2303 16.1264 21.0441 14.7348 20.4139 13.6095L19.5622 12.0886C19.2825 11.5891 19.1479 11.0264 19.1783 10.4571L19.2063 9.93291C19.3902 5.79512 16.1133 2.3999 11.9998 2.3999Z" />
                                    <path d="M9.59961 18.5264C9.59961 19.8519 10.6741 20.9264 11.9996 20.9264C13.3251 20.9264 14.3996 19.8519 14.3996 18.5264" strokeLinecap="round" />
                                </svg>
                                {reminder ? (typeof reminder === 'number' ? `提前${reminder}分钟` : reminderOptions.find(o => o.value === reminder)?.label) : ''}
                            </button>
                        </ReminderPicker>
                        
                        <PriorityPicker 
                            value={priority} 
                            onChange={setPriority}
                        >
                            <button
                                type="button"
                                className={`flex items-center gap-1 text-xs px-2 py-1.5 rounded-lg border transition-colors ${
                                    priority 
                                    ? 'bg-slate-50 border-slate-300 dark:bg-slate-800 dark:border-slate-600' 
                                    : 'bg-white text-slate-500 border-slate-200 hover:border-slate-300 dark:bg-slate-800 dark:border-slate-700 dark:text-slate-400'
                                }`}
                                title="设置优先级"
                            >
                                <Flag size={12} className={priority ? priorityOptions.find(o => o.value === priority)?.color : ''} fill={priority ? 'currentColor' : 'none'} />
                                {priority ? priorityOptions.find(o => o.value === priority)?.label : ''}
                            </button>
                        </PriorityPicker>
                    </div>
                    
                    <div className="flex items-center gap-2 w-full sm:w-auto justify-end">
                        {(newTodo.trim() || isDraftMode) && (
                            <button
                                type="button"
                                onClick={() => setIsDraftMode(!isDraftMode)}
                                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors border ${
                                    isDraftMode 
                                    ? 'bg-blue-50 text-blue-600 border-blue-200' 
                                    : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                                }`}
                            >
                                {isDraftMode ? '收起子任务' : (subTodos.length > 0 ? `查看子任务 (${subTodos.length})` : '添加子项任务')}
                            </button>
                        )}

                        <button 
                            type="submit"
                            disabled={!newTodo.trim()}
                            className="px-4 py-1.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-1 shadow-sm"
                        >
                            <Plus size={16} /> 添加
                        </button>
                    </div>
                </div>
            </form>
        </div>
    );
});

// 2. SubTodoItem
const SubTodoItem = memo(({ 
    subTodo, 
    parentId, 
    onToggle, 
    onDelete,
    onEdit
}: { 
    subTodo: SubTodo, 
    parentId: string, 
    onToggle: (pid: string, sid: string) => void, 
    onDelete: (pid: string, sid: string) => void,
    onEdit: (pid: string, sid: string, text: string) => void
}) => {
    const [isEditing, setIsEditing] = useState(false);
    const [editText, setEditText] = useState(subTodo.text);
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (isEditing) {
            inputRef.current?.focus();
        }
    }, [isEditing]);

    const handleEditSubmit = () => {
        if (editText.trim()) {
            onEdit(parentId, subTodo.id, editText.trim());
            setIsEditing(false);
        } else {
            // Revert if empty
            setEditText(subTodo.text);
            setIsEditing(false);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            handleEditSubmit();
        }
        if (e.key === 'Escape') {
            handleEditSubmit(); // Save and exit
        }
    };

    return (
        <div className="flex items-center gap-2 pl-2 py-1 group/sub min-h-[28px] hover:bg-slate-50 dark:hover:bg-slate-700/50 rounded transition-colors -ml-2 pr-2">
            <button 
                onClick={(e) => {
                    onToggle(parentId, subTodo.id);
                }}
                className={`flex-shrink-0 ${subTodo.completed ? 'text-green-500' : 'text-slate-300 hover:text-slate-500 dark:hover:text-slate-400'}`}
            >
                {subTodo.completed ? <CheckCircle2 size={14} /> : <Circle size={14} />}
            </button>
            
            {isEditing ? (
                <input
                    ref={inputRef}
                    type="text"
                    value={editText}
                    onChange={(e) => setEditText(e.target.value)}
                    onBlur={handleEditSubmit}
                    onKeyDown={handleKeyDown}
                    className="flex-1 text-sm bg-transparent border-b border-blue-500 outline-none text-slate-700 dark:text-slate-300 px-0 py-0 h-[20px] leading-[20px]"
                />
            ) : (
                <span 
                    className={`text-sm flex-1 px-0 py-0 h-[20px] leading-[20px] border-b border-transparent truncate block ${subTodo.completed ? 'line-through text-slate-400' : 'text-slate-700 dark:text-slate-300 cursor-text'}`}
                    onClick={() => !subTodo.completed && setIsEditing(true)}
                    title={!subTodo.completed ? "点击编辑" : ""}
                >
                    {subTodo.text || <span className="text-slate-300 italic">空任务</span>}
                </span>
            )}

            {!isEditing && (
                <div className="flex items-center opacity-0 group-hover/sub:opacity-100 transition-opacity">
                    {!subTodo.completed && (
                        <button 
                            onClick={(e) => {
                                e.stopPropagation();
                                setIsEditing(true);
                            }}
                            className="text-slate-400 hover:text-blue-500 p-1"
                            title="编辑子任务"
                        >
                            <Edit2 size={12} />
                        </button>
                    )}
                    <button 
                        onClick={(e) => {
                            e.stopPropagation();
                            onDelete(parentId, subTodo.id);
                        }}
                        className="text-slate-400 hover:text-red-500 p-1"
                        title="删除子任务"
                    >
                        <Trash2 size={12} />
                    </button>
                </div>
            )}
        </div>
    );
});

// 3. TodoItem
const TodoItem = memo(({ 
    todo, 
    onToggle, 
    onDelete,
    onAddSubTodo,
    onToggleSubTodo,
    onDeleteSubTodo,
    onUpdateReminder,
    onUpdateDueDate,
    onUpdateText,
    onUpdateSubTodoText,
    onUpdatePriority
}: { 
    todo: Todo, 
    onToggle: (id: string) => void, 
    onDelete: (id: string) => void,
    onAddSubTodo: (id: string, text: string) => void,
    onToggleSubTodo: (pid: string, sid: string) => void,
    onDeleteSubTodo: (pid: string, sid: string) => void,
    onUpdateReminder: (id: string, timing: ReminderTiming) => void,
    onUpdateDueDate: (id: string, date: number | undefined) => void,
    onUpdateText: (id: string, text: string) => void,
    onUpdateSubTodoText: (pid: string, sid: string, text: string) => void,
    onUpdatePriority: (id: string, priority: 'high' | 'medium' | 'low' | undefined) => void
}) => {
    const [newSubTodo, setNewSubTodo] = useState('');
    const [showSubInput, setShowSubInput] = useState(false);
    const [showSubTasks, setShowSubTasks] = useState(false);
    
    // Editing state for main todo
    const [isEditing, setIsEditing] = useState(false);
    const [editText, setEditText] = useState(todo.text);
    const editInputRef = useRef<HTMLInputElement>(null);
    const itemRef = useRef<HTMLDivElement>(null);
    const [isMobileView, setIsMobileView] = useState(() => typeof window !== 'undefined' && window.innerWidth < 768);
    const [showActionBar, setShowActionBar] = useState(false);

    // Countdown logic
    const [timeLeft, setTimeLeft] = useState<string | null>(null);

    // Delayed completion state
    const [isMarkingCompleted, setIsMarkingCompleted] = useState(false);
    const completionTimerRef = useRef<NodeJS.Timeout | null>(null);

    // Derived completed state for visual rendering
    const isCompleted = todo.completed || isMarkingCompleted;

    // Cleanup timer on unmount
    useEffect(() => {
        return () => {
            if (completionTimerRef.current) clearTimeout(completionTimerRef.current);
        };
    }, []);

    useEffect(() => {
        const onResize = () => setIsMobileView(window.innerWidth < 768);
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);

    useEffect(() => {
        if (!isMobileView) {
            setShowActionBar(true);
            return;
        }
        setShowActionBar(false);
    }, [isMobileView, todo.id]);

    useEffect(() => {
        if (!isMobileView || !showActionBar) return;
        const onDocClick = (e: MouseEvent | TouchEvent) => {
            const target = e.target as Node;
            if (itemRef.current && !itemRef.current.contains(target)) {
                if (showSubInput && newSubTodo.trim()) {
                    onAddSubTodo(todo.id, newSubTodo.trim());
                }
                setShowActionBar(false);
                setShowSubInput(false);
                setNewSubTodo('');
                if (isEditing) {
                    setEditText(todo.text);
                    setIsEditing(false);
                }
            }
        };
        document.addEventListener('mousedown', onDocClick);
        document.addEventListener('touchstart', onDocClick);
        return () => {
            document.removeEventListener('mousedown', onDocClick);
            document.removeEventListener('touchstart', onDocClick);
        };
    }, [isMobileView, showActionBar, showSubInput, newSubTodo, isEditing, todo.text, onAddSubTodo, todo.id]);

    useEffect(() => {
        if (isMobileView && isEditing) {
            setShowActionBar(true);
        }
    }, [isMobileView, isEditing]);

    const handleToggleClick = () => {
        if (todo.completed) {
            // If already completed (real state), toggle immediately (uncheck)
            onToggle(todo.id);
        } else {
            // If active
            if (isMarkingCompleted) {
                // If currently waiting to be marked completed, cancel it
                if (completionTimerRef.current) {
                    clearTimeout(completionTimerRef.current);
                    completionTimerRef.current = null;
                }
                setIsMarkingCompleted(false);
            } else {
                // Start delayed completion
                setIsMarkingCompleted(true);
                completionTimerRef.current = setTimeout(() => {
                    onToggle(todo.id);
                    setIsMarkingCompleted(false);
                    completionTimerRef.current = null;
                }, 500); // 500ms delay
            }
        }
    };

    useEffect(() => {
        // Only clear timeLeft if actually completed (data state), not just marking (visual state)
        if (!todo.dueDate || todo.completed) {
            setTimeLeft(null);
            return;
        }

        const updateTimer = () => {
            const now = Date.now();
            const diff = (todo.dueDate || 0) - now;
            
            if (diff <= 0) {
                const overdue = Math.abs(diff);
                const hours = Math.floor(overdue / (1000 * 60 * 60));
                const minutes = Math.floor((overdue % (1000 * 60 * 60)) / (1000 * 60));
                const seconds = Math.floor((overdue % (1000 * 60)) / 1000);
                setTimeLeft(`已逾期 ${hours}小时${minutes}分${seconds}秒`);
            } else if (diff < 24 * 60 * 60 * 1000) {
                const hours = Math.floor(diff / (1000 * 60 * 60));
                const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
                const seconds = Math.floor((diff % (1000 * 60)) / 1000);
                setTimeLeft(`${hours}小时${minutes}分${seconds}秒 到期`);
            } else {
                setTimeLeft(null);
            }
        };

        updateTimer();
        const interval = setInterval(updateTimer, 1000); // Update every second
        return () => clearInterval(interval);
    }, [todo.dueDate, todo.completed]);

    useEffect(() => {
        if (isEditing) {
            editInputRef.current?.focus();
        }
    }, [isEditing]);

    const handleEditSubmit = () => {
        if (editText.trim()) {
            onUpdateText(todo.id, editText.trim());
            setIsEditing(false);
        } else {
            setEditText(todo.text);
            setIsEditing(false);
        }
    };

    const handleEditKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            handleEditSubmit();
        }
    };

    const handleAddSub = (e: React.FormEvent) => {
        e.preventDefault();
        if (newSubTodo.trim()) {
            onAddSubTodo(todo.id, newSubTodo);
            setNewSubTodo('');
        }
    };

    const isSimple = !todo.subTodos?.length && !todo.dueDate && !todo.reminderTiming && !todo.priority;

    const priorityConfig = {
        high: { color: 'text-red-500', label: '高' },
        medium: { color: 'text-blue-500', label: '中' },
        low: { color: 'text-green-500', label: '低' }
    };

    return (
        <motion.div 
            // Removed layout prop to prevent jitter on subtask updates
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            className="relative py-1 group"
            ref={itemRef}
        >
            {/* Timeline Dot */}
            <div className={`hidden md:block absolute left-[-11px] top-[26px] -translate-y-1/2 w-2 h-2 rounded-full ring-2 ring-slate-50 dark:ring-slate-900 z-10 transition-colors ${
                isCompleted 
                ? 'bg-green-500' 
                : timeLeft?.startsWith('已逾期') 
                    ? 'bg-red-500' 
                    : 'bg-blue-400'
            }`}></div>

            <div className={`bg-white dark:bg-slate-800 rounded-lg border border-slate-100 dark:border-white/5 shadow-sm hover:shadow-md transition-all overflow-visible relative min-h-[52px] flex flex-col ${isMobileView && showActionBar ? 'ring-1 ring-blue-200 dark:ring-blue-700/40' : ''}`}>
                <div className={`flex flex-col w-full gap-2 flex-1 ${isSimple ? 'justify-center pt-3 pb-1 px-2' : 'p-2'}`}>
                    <div 
                        className={`flex gap-3 w-full group/main rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors p-1 -ml-1 ${isSimple ? 'items-center' : 'items-start'}`}
                        onClick={() => {
                            if (isMobileView) setShowActionBar(true);
                        }}
                    >
                        <button 
                            onClick={handleToggleClick}
                            className={`flex-shrink-0 ${isCompleted ? 'text-green-500' : 'text-slate-400 hover:text-slate-600 dark:hover:text-slate-300'} ${isSimple ? '' : 'mt-0.5'}`}
                        >
                        {isCompleted ? <CheckCircle2 size={20} /> : <Circle size={20} />}
                    </button>
                    
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 min-h-[24px]">
                            {/* Priority Icon */}
                            {!isCompleted && todo.priority && (
                                <Flag size={14} className={priorityConfig[todo.priority]?.color} fill="currentColor" />
                            )}
                            
                            {isEditing ? (
                                <input
                                    ref={editInputRef}
                                    type="text"
                                    value={editText}
                                    onChange={(e) => setEditText(e.target.value)}
                                    onBlur={handleEditSubmit}
                                    onKeyDown={handleEditKeyDown}
                                    className="flex-1 text-sm bg-transparent border-b border-blue-500 outline-none text-slate-800 dark:text-slate-200 px-0 py-0 h-[24px] leading-[24px]"
                                />
                            ) : (
                                <span 
                                    className={`text-sm flex-1 px-0 py-0 h-[24px] leading-[24px] border-b border-transparent truncate ${isCompleted ? 'line-through text-slate-400' : 'text-slate-800 dark:text-slate-200 cursor-text'}`}
                                    onClick={() => {
                                        if (!isCompleted) {
                                            if (isMobileView) setShowActionBar(true);
                                            setIsEditing(true);
                                        }
                                    }}
                                    title={!isCompleted ? "点击编辑" : ""}
                                >
                                    {todo.text}
                                </span>
                            )}
                            
                            {/* Edit Button for Main Todo - Show only on hover and not editing */}
                            {!isEditing && !isCompleted && (
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        if (isMobileView) setShowActionBar(true);
                                        setIsEditing(true);
                                    }}
                                    className="opacity-0 group-hover/main:opacity-100 text-slate-400 hover:text-blue-500 p-0.5 transition-opacity"
                                    title="编辑"
                                >
                                    <Edit2 size={12} />
                                </button>
                            )}

            {/* Countdown Badge - Use todo.completed to keep badge visible during delay */}
            {timeLeft && !todo.completed && !isMobileView && (
                <span className="text-[10px] px-1.5 py-0.5 bg-red-50 text-red-500 rounded-full flex items-center gap-1 border border-red-100">
                    <Timer size={10} />
                    {timeLeft}
                </span>
            )}

            {/* Completion Date - Show only when completed */}
            {isCompleted && todo.completedAt && (
                <span className="text-[10px] text-slate-400 flex items-center gap-1">
                    <CheckCircle2 size={10} />
                    {new Date(todo.completedAt).toLocaleString(undefined, { month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                </span>
            )}
                        </div>
                        
                        {(todo.dueDate || todo.reminderTiming || (todo.subTodos && todo.subTodos.length > 0)) && (
                            <div className="flex items-center gap-3 mt-1 min-h-[20px]">
                                {todo.dueDate && (
                                    <div className={`text-xs flex items-center gap-1 ${
                                        // Keep red color even if marking completed, only change when actually completed
                                        todo.dueDate < Date.now() && !todo.completed ? 'text-red-500' : 'text-slate-400'
                                    }`}>
                                        <Clock size={12} />
                                        {isMobileView ? '预期时间' : new Date(todo.dueDate).toLocaleString()}
                                    </div>
                                )}
                                
                                {/* Reminder Indicator */}
                                {todo.reminderTiming && (
                                    <div className="text-xs flex items-center gap-1 text-blue-500 bg-blue-50 px-1.5 py-0.5 rounded">
                                        <Bell size={10} />
                                        <span>{
                                            typeof todo.reminderTiming === 'number' ? `-${todo.reminderTiming}m` :
                                            todo.reminderTiming === '1h' ? '-1h' : 
                                            todo.reminderTiming === '12h' ? '-12h' : 
                                            todo.reminderTiming === '1d' ? '-1d' : ''
                                        }</span>
                                    </div>
                                )}

                                {/* Subtasks Indicator / Toggle */}
                                {todo.subTodos && todo.subTodos.length > 0 && (
                                    <button 
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setShowSubTasks(!showSubTasks);
                                        }}
                                        className="text-xs flex items-center gap-1 text-slate-500 bg-slate-100 dark:bg-slate-700/50 px-1.5 py-0.5 rounded hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
                                    >
                                        <CornerDownRight size={10} />
                                        <span>{todo.subTodos.filter(s => s.completed).length}/{todo.subTodos.length}</span>
                                        <ChevronDown size={10} className={`transform transition-transform ${showSubTasks ? 'rotate-180' : ''}`} />
                                    </button>
                                )}
                            </div>
                        )}

                    </div>
                    </div>

                    <AnimatePresence>
                    {showSubTasks && (
                    <motion.div 
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="pl-[32px] w-full overflow-hidden"
                    >
                        {/* Sub-todos list */}
                        {todo.subTodos && todo.subTodos.length > 0 && (
                            <div className="space-y-0.5 border-l-2 border-slate-100 dark:border-slate-700 pl-2">
                                {todo.subTodos.map(st => (
                                    <SubTodoItem 
                                        key={st.id} 
                                        subTodo={st} 
                                        parentId={todo.id} 
                                        onToggle={onToggleSubTodo} 
                                        onDelete={onDeleteSubTodo} 
                                        onEdit={onUpdateSubTodoText}
                                    />
                                ))}
                            </div>
                        )}
                        
                        {/* Add Sub-todo Input */}
                        <AnimatePresence>
                            {showSubInput && (
                                <motion.form 
                                    initial={{ opacity: 0, height: 0 }}
                                    animate={{ opacity: 1, height: 'auto' }}
                                    exit={{ opacity: 0, height: 0 }}
                                    onSubmit={handleAddSub} 
                                    className="mt-2 flex items-center gap-2 pl-2"
                                >
                                    <button
                                        type="submit"
                                        disabled={!newSubTodo.trim()}
                                        className={`p-1 rounded transition-colors ${
                                            newSubTodo.trim()
                                                ? 'text-green-500 hover:text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20'
                                                : 'text-slate-300 cursor-not-allowed'
                                        }`}
                                        title="提交子任务"
                                    >
                                        <CheckCircle2 size={14} />
                                    </button>
                                    <input
                                        type="text"
                                        value={newSubTodo}
                                        onChange={(e) => setNewSubTodo(e.target.value)}
                                        onKeyDown={(e) => {
                                        if (e.key === 'Escape') {
                                            setShowSubInput(false);
                                        }
                                    }}
                                    placeholder="添加子任务..."
                                        className="flex-1 text-xs bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded px-2 py-1 outline-none text-slate-700 dark:text-slate-300"
                                        autoFocus
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowSubInput(false)}
                                        className="p-1 text-slate-400 hover:text-red-500"
                                        title="取消"
                                    >
                                        <X size={14} />
                                    </button>
                                </motion.form>
                            )}
                        </AnimatePresence>
                    </motion.div>
                    )}
                    </AnimatePresence>

                    {(!isMobileView || showActionBar) && (
                    <div className={`flex items-center justify-end gap-1 mt-2 lg:mt-0 opacity-100 lg:opacity-0 lg:group-hover:opacity-100 focus-within:opacity-100 transition-opacity bg-white dark:bg-slate-800 lg:shadow-sm lg:border border-slate-100 dark:border-slate-700 rounded-lg p-1 static lg:absolute lg:right-2 lg:top-2 z-20 ${
                        // Hide main tools if hovering sub-task to avoid clutter, BUT we moved sub-task tools inline so maybe fine?
                        // Actually user said "hover已完成的，仅显示删除按钮即可"
                        isCompleted ? '' : 'group-hover/sub:opacity-0' 
                    }`}>
                        {!isCompleted && (
                            <>
                                {/* Add Subtask */}
                                <button
                                    onClick={() => {
                                        setShowSubTasks(true);
                                        setShowSubInput(!showSubInput);
                                    }}
                                    className={`p-1.5 rounded hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-400 hover:text-blue-500 transition-colors ${showSubInput ? 'text-blue-500 bg-slate-50' : ''}`}
                                    title="添加子项"
                                >
                                    <Plus size={14} />
                                </button>

                                {/* Reminder Config */}
                                <ReminderPicker
                                    value={todo.reminderTiming || null}
                                    onChange={(val) => onUpdateReminder(todo.id, val)}
                                >
                                    <button
                                        type="button"
                                        className={`p-1.5 rounded hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-400 hover:text-purple-500 transition-colors ${todo.reminderTiming ? 'text-purple-500 bg-slate-50' : ''}`}
                                        title="设置提醒"
                                    >
                                        <Bell size={14} />
                                    </button>
                                </ReminderPicker>

                                {/* Priority Config */}
                                <PriorityPicker
                                    value={todo.priority}
                                    onChange={(val) => onUpdatePriority(todo.id, val)}
                                >
                                    <button
                                        type="button"
                                        className={`p-1.5 rounded hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-400 hover:text-orange-500 transition-colors ${todo.priority ? 'bg-slate-50' : ''}`}
                                        title="设置优先级"
                                    >
                                        <Flag size={14} className={todo.priority ? priorityConfig[todo.priority]?.color : ''} fill={todo.priority ? 'currentColor' : 'none'} />
                                    </button>
                                </PriorityPicker>

                                {/* Date Picker Trigger */}
                                <div className="relative">
                                    <DatePicker 
                                        value={todo.dueDate ? new Date(todo.dueDate) : null}
                                        onChange={(date) => onUpdateDueDate(todo.id, date ? date.getTime() : undefined)}
                                        className="text-slate-400 hover:text-orange-500 p-1.5 rounded hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center justify-center"
                                        placeholder="" 
                                    />
                                </div>
                            </>
                        )}

                        {/* Delete */}
                        <button 
                            onClick={() => onDelete(todo.id)}
                            className={`p-1.5 rounded hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-400 hover:text-red-500 transition-colors ml-1 pl-2 ${!todo.completed ? 'border-l border-slate-100 dark:border-slate-700' : ''}`}
                            title="删除"
                        >
                            <Trash2 size={14} />
                        </button>
                    </div>
                    )}
                </div>
            </div>
        </motion.div>
    );
});

// --- Standalone Components ---

// Timeline Group Component
const TimelineGroup = memo(({ 
    title, 
    todos, 
    colorClass, 
    icon: Icon,
    onToggle,
    onDelete,
    onAddSubTodo,
    onToggleSubTodo,
    onDeleteSubTodo,
    onUpdateReminder,
    onUpdateDueDate,
    onUpdateText,
    onUpdateSubTodoText,
    onUpdatePriority,
    hideTitle,
    id,
    defaultCollapsed = true
}: { 
    title: string, 
    todos: Todo[], 
    colorClass: string, 
    icon: any,
    onToggle: (id: string) => void,
    onDelete: (id: string) => void,
    onAddSubTodo: (id: string, text: string) => void,
    onToggleSubTodo: (pid: string, sid: string) => void,
    onDeleteSubTodo: (pid: string, sid: string) => void,
    onUpdateReminder: (id: string, timing: ReminderTiming) => void,
    onUpdateDueDate: (id: string, date: number | undefined) => void,
    onUpdateText: (id: string, text: string) => void,
    onUpdateSubTodoText: (pid: string, sid: string, text: string) => void,
    onUpdatePriority: (id: string, priority: 'high' | 'medium' | 'low' | undefined) => void,
    hideTitle?: boolean,
    id: string,
    defaultCollapsed?: boolean
}) => {
    const [isCollapsed, setIsCollapsed] = useState(() => {
        try {
            const key = `todo_group_collapsed_${id}`;
            const stored = localStorage.getItem(key);
            if (stored !== null) {
                return JSON.parse(stored);
            }
        } catch (e) {
            console.warn('Failed to read local storage', e);
        }
        return defaultCollapsed;
    });

    const handleToggle = () => {
        const newState = !isCollapsed;
        setIsCollapsed(newState);
        try {
            localStorage.setItem(`todo_group_collapsed_${id}`, JSON.stringify(newState));
        } catch (e) {
            console.warn('Failed to save to local storage', e);
        }
    };
    
    if (todos.length === 0) return null;
    
    return (
        <div className="relative pl-0 md:pl-6 pb-2">
            {/* Vertical Line */}
            <div className="hidden md:block absolute left-[-8px] top-0 -bottom-2 w-px bg-slate-200 dark:bg-slate-700"></div>
            
            {/* Group Header Node */}
            {!hideTitle && (
                <div className={`hidden md:block absolute left-[-11px] top-2.5 w-2 h-2 rounded-full ring-2 ring-white dark:ring-slate-900 ${colorClass}`}></div>
            )}

            {!hideTitle && (
                <div 
                    className="flex items-center gap-2 mb-2 cursor-pointer select-none group/header"
                    onClick={handleToggle}
                >
                    <button className={`p-0.5 rounded hover:bg-slate-200 dark:hover:bg-slate-700 transition-transform ${isCollapsed ? '-rotate-90' : ''}`}>
                        <ChevronDown size={14} className="text-slate-400" />
                    </button>
                    <h3 className={`text-sm font-semibold flex items-center gap-2 ${colorClass.replace('bg-', 'text-')}`}>
                        <Icon size={16} /> {title}
                        <span className="text-xs font-normal text-slate-400 bg-slate-100 dark:bg-slate-800 px-2 py-0.5 rounded-full">
                            {todos.length}
                        </span>
                    </h3>
                </div>
            )}
            
            <AnimatePresence>
                {!isCollapsed && (
                    <motion.div 
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="space-y-3 overflow-hidden"
                    >
                        {todos.map(todo => (
                            <TodoItem 
                                key={todo.id} 
                                todo={todo} 
                                onToggle={onToggle} 
                                onDelete={onDelete}
                                onAddSubTodo={onAddSubTodo}
                                onToggleSubTodo={onToggleSubTodo}
                                onDeleteSubTodo={onDeleteSubTodo}
                                onUpdateReminder={onUpdateReminder}
                                onUpdateDueDate={onUpdateDueDate}
                                onUpdateText={onUpdateText}
                                onUpdateSubTodoText={onUpdateSubTodoText}
                                onUpdatePriority={onUpdatePriority}
                            />
                        ))}
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
});

const CompletedGroup = memo(({ 
    todos,
    onToggle,
    onDelete,
    onAddSubTodo,
    onToggleSubTodo,
    onDeleteSubTodo,
    onUpdateReminder,
    onUpdateDueDate,
    onUpdateText,
    onUpdateSubTodoText,
    onUpdatePriority
}: { 
    todos: Todo[],
    onToggle: (id: string) => void,
    onDelete: (id: string) => void,
    onAddSubTodo: (id: string, text: string) => void,
    onToggleSubTodo: (pid: string, sid: string) => void,
    onDeleteSubTodo: (pid: string, sid: string) => void,
    onUpdateReminder: (id: string, timing: ReminderTiming) => void,
    onUpdateDueDate: (id: string, date: number | undefined) => void,
    onUpdateText: (id: string, text: string) => void,
    onUpdateSubTodoText: (pid: string, sid: string, text: string) => void,
    onUpdatePriority: (id: string, priority: 'high' | 'medium' | 'low' | undefined) => void
}) => {
    const [isCollapsed, setIsCollapsed] = useState(true); 
    
    return (
      <>
          <div 
              className="flex items-center gap-2 mb-3 cursor-pointer select-none group/header"
              onClick={() => setIsCollapsed(!isCollapsed)}
          >
              <button className={`p-0.5 rounded hover:bg-slate-200 dark:hover:bg-slate-700 transition-transform ${isCollapsed ? '-rotate-90' : ''}`}>
                  <ChevronDown size={14} className="text-slate-400" />
              </button>
              <h3 className="text-sm font-semibold text-green-500 flex items-center gap-2">
                  <CheckCircle2 size={16} /> 已完成 ({todos.length})
              </h3>
          </div>
          
          <AnimatePresence>
              {!isCollapsed && (
                  <motion.div 
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="space-y-2 opacity-60 hover:opacity-100 transition-opacity overflow-hidden"
                  >
                  {todos.map(todo => (
                      <TodoItem 
                          key={todo.id} 
                          todo={todo} 
                          onToggle={onToggle} 
                          onDelete={onDelete}
                          onAddSubTodo={onAddSubTodo}
                          onToggleSubTodo={onToggleSubTodo}
                          onDeleteSubTodo={onDeleteSubTodo}
                          onUpdateReminder={onUpdateReminder}
                          onUpdateDueDate={onUpdateDueDate}
                          onUpdateText={onUpdateText}
                          onUpdateSubTodoText={onUpdateSubTodoText}
                          onUpdatePriority={onUpdatePriority}
                      />
                  ))}
                  </motion.div>
              )}
          </AnimatePresence>
      </>
    );
});

const TodoStats = ({ todos, compact = false }: { todos: Todo[], compact?: boolean }) => {
    // Dynamic timer for overdue calculation
    const [now, setNow] = useState(new Date());

    useEffect(() => {
        // Update every minute to keep overdue time fresh
        const interval = setInterval(() => {
            setNow(new Date());
        }, 60000);
        return () => clearInterval(interval);
    }, []);

    const stats = useMemo(() => {
        const total = todos.length;
        const totalSubtasks = todos.reduce((acc, t) => acc + (t.subTodos?.length || 0), 0);
        
        const nowTime = now.getTime();
        const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
        const tomorrowStart = todayStart + 86400000;
        
        // Completed
        const completedTasks = todos.filter(t => t.completed);
        const completedCount = completedTasks.length;
        const completionRate = total > 0 ? Math.round((completedCount / total) * 100) : 0;

        // Today: All tasks due today
        const todayTasks = todos.filter(t => !t.completed && t.dueDate && t.dueDate >= todayStart && t.dueDate < tomorrowStart);
        const todayTotal = todayTasks.length;
        const todayCompleted = todos.filter(t => t.completed && t.dueDate && t.dueDate >= todayStart && t.dueDate < tomorrowStart).length;

        // Long-term (No Date)
        const longTermTasks = todos.filter(t => !t.completed && !t.dueDate);
        const longTermCount = longTermTasks.length;
        const longTermSubtasks = longTermTasks.reduce((acc, t) => acc + (t.subTodos?.length || 0), 0);
        
        // Overdue: Due before today and not completed
        const overdueTasks = todos.filter(t => !t.completed && t.dueDate && t.dueDate < todayStart);
        const overdueCount = overdueTasks.length;
        
        let overdueTimeStr = '';
        if (overdueCount > 0) {
            let totalOverdueTime = 0;
            overdueTasks.forEach(t => {
                if (t.dueDate) {
                    totalOverdueTime += (nowTime - t.dueDate);
                }
            });
            const hours = Math.floor(totalOverdueTime / (1000 * 60 * 60));
            const minutes = Math.floor((totalOverdueTime % (1000 * 60 * 60)) / (1000 * 60));
            overdueTimeStr = `${hours}小时${minutes}分`;
        }

        // Upcoming (Future: Tomorrow onwards)
        const upcomingTasks = todos.filter(t => !t.completed && t.dueDate && t.dueDate >= tomorrowStart);
        const upcomingTotal = upcomingTasks.length;
        
        // Sub-counts for Upcoming
        const tomorrowTasks = upcomingTasks.filter(t => t.dueDate && t.dueDate < tomorrowStart + 86400000);
        const tomorrowCount = tomorrowTasks.length;
        
        return { 
            total, totalSubtasks, 
            completedCount, completionRate,
            todayTotal, todayCompleted, 
            longTermCount, longTermSubtasks,
            upcomingTotal, tomorrowCount, 
            overdueCount, overdueTimeStr 
        };
    }, [todos, now]);

    const statItems = [
        { 
            label: '今日待办', 
            value: stats.todayTotal, 
            subValue: stats.todayCompleted,
            type: 'today',
            icon: Calendar, 
            color: 'text-white', 
            bg: 'bg-white/20',
            containerClass: 'bg-gradient-to-br from-blue-500 to-indigo-600 text-white border-none shadow-md'
        },
        { 
            label: '即将待办', 
            value: stats.upcomingTotal, 
            subValue: stats.tomorrowCount,
            type: 'upcoming',
            icon: Clock,
            color: 'text-purple-500', 
            bg: 'bg-purple-50 dark:bg-purple-900/20' 
        },
        { 
            label: '逾期待办', 
            value: stats.overdueCount, 
            subText: stats.overdueTimeStr,
            type: 'overdue',
            icon: AlertCircle, 
            color: 'text-red-500', 
            bg: 'bg-red-50 dark:bg-red-900/20' 
        },
        { 
            label: '长期规划', 
            value: stats.longTermCount, 
            subValue: stats.longTermSubtasks,
            type: 'longterm',
            icon: CalendarOff, 
            color: 'text-indigo-500', 
            bg: 'bg-indigo-50 dark:bg-indigo-900/20' 
        },
        { 
            label: '已完成', 
            value: `${stats.completionRate}%`, 
            subValue: stats.completedCount,
            type: 'completed',
            icon: CheckCircle2, 
            color: 'text-green-500', 
            bg: 'bg-green-50 dark:bg-green-900/20' 
        },
        { 
            label: '全部待办', 
            value: stats.total, 
            subValue: stats.totalSubtasks,
            type: 'all',
            icon: ListTodo, 
            color: 'text-slate-600 dark:text-slate-300', 
            bg: 'bg-slate-100 dark:bg-slate-700/50' 
        },
    ];

    if (compact) {
        return (
            <div className="grid grid-cols-3 gap-2 w-full px-1">
                {statItems.map((item, index) => (
                    <div key={index} className={`relative overflow-hidden px-2.5 py-2 rounded-lg border border-slate-100 dark:border-white/5 flex flex-col items-start justify-center min-h-[56px] ${item.containerClass || 'bg-white dark:bg-slate-800'}`}>
                        {/* Background Icon Decoration */}
                        <div className={`absolute -right-1 -bottom-1 opacity-10 pointer-events-none transform rotate-12 ${item.containerClass ? 'text-white' : item.color}`}>
                            <item.icon size={32} />
                        </div>
                        
                        <div className={`text-[10px] font-medium leading-none mb-1 relative z-10 ${item.containerClass ? 'text-blue-50' : 'text-slate-400'}`}>
                            {item.label}
                        </div>
                        
                        <div className={`text-lg font-bold leading-none relative z-10 flex items-baseline gap-0.5 ${item.color}`}>
                            {item.value}
                        </div>
                    </div>
                ))}
            </div>
        );
    }

    return (
        <div className="grid grid-cols-2 gap-3">
            {statItems.map((item, index) => (
                <div key={index} className={`p-4 rounded-xl border border-slate-100 dark:border-white/5 shadow-sm hover:shadow-md transition-all group relative overflow-hidden h-[90px] flex flex-col justify-between ${item.containerClass || 'bg-white dark:bg-slate-800'}`}>
                    
                    {/* Decorative Big Icon */}
                    <div className={`absolute -right-4 -bottom-4 opacity-[0.08] group-hover:opacity-[0.12] transition-opacity transform rotate-12 pointer-events-none ${item.containerClass ? 'text-white' : item.color}`}>
                        <item.icon size={80} />
                    </div>

                    <div className="relative z-10 flex flex-col h-full justify-between">
                        <div className="flex items-center gap-1.5">
                            <div className={`p-1 rounded-md ${item.bg} ${item.color}`}>
                                <item.icon size={14} />
                            </div>
                            <span className={`text-xs font-medium ${item.containerClass ? 'text-blue-50' : 'text-slate-500 dark:text-slate-400'}`}>
                                {item.label}
                            </span>
                        </div>

                        <div className="flex items-end gap-2">
                            <div className={`text-3xl font-bold tracking-tight leading-none ${item.color}`}>
                                {item.value}
                            </div>
                            
                            <div className="flex items-center pb-0.5">
                                {/* Specific Sub-stats - Inline Layout */}
                                {item.type === 'today' && (
                                    <div className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-white/20 text-white backdrop-blur-sm border border-white/10">
                                        <CheckCircle2 size={10} />
                                        <span>已完成 {item.subValue}</span>
                                    </div>
                                )}

                                {item.type === 'all' && (
                                    <div className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-slate-100 dark:bg-slate-700/50 text-slate-500 dark:text-slate-400">
                                        <CornerDownRight size={10} />
                                        <span>子任务 {item.subValue}</span>
                                    </div>
                                )}

                                {item.type === 'upcoming' && (
                                    <div className="flex items-center gap-1 min-w-0">
                                        {/* @ts-ignore */}
                                        {item.subValue > 0 && (
                                            <div className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-purple-100 dark:bg-purple-900/40 text-purple-600 dark:text-purple-300 whitespace-nowrap">
                                                <Calendar size={10} />
                                                {/* @ts-ignore */}
                                                <span>明天 {item.subValue}</span>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {item.type === 'longterm' && (
                                    <div className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-indigo-100 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-300">
                                        <CornerDownRight size={10} />
                                        <span>子项 {item.subValue}</span>
                                    </div>
                                )}

                                {item.type === 'completed' && (
                                    <div className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-green-100 dark:bg-green-900/40 text-green-600 dark:text-green-300">
                                        <CheckCircle2 size={10} />
                                        <span>总完成 {item.subValue}</span>
                                    </div>
                                )}
                                
                                {item.type === 'overdue' && item.subText && (
                                    <div className="inline-flex items-center gap-1 text-[10px] text-red-500 bg-red-50 dark:bg-red-900/20 px-1.5 py-0.5 rounded">
                                        <Clock size={10} />
                                        <span className="truncate max-w-[80px]" title={`累计逾期: ${item.subText}`}>
                                            {item.subText}
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );
};

// 4. Main Page Component
const Todos = () => {
  const { 
      todos, 
      addTodo, 
      toggleTodo, 
      deleteTodo, 
      addSubTodo, 
      toggleSubTodo, 
      deleteSubTodo,
      updateTodoReminder,
      updateTodoDueDate,
      updateTodoText,
      updateSubTodoText,
      updateTodoPriority
  } = useTodos();

  const [settings, setSettings] = useState<AppSettings | null>(null);
  const [isMobileComposerOpen, setIsMobileComposerOpen] = useState(false);
  useEffect(() => {
      getSettings().then(setSettings);
  }, []);

  const handleAddTodo = (text: string, date: Date | null, reminder: ReminderTiming, subTodos: string[], priority?: 'high' | 'medium' | 'low') => {
    addTodo(text, date ? date.toISOString() : undefined, false, reminder, subTodos, priority);
  };

  const handleAddTodoFromMobile = (text: string, date: Date | null, reminder: ReminderTiming, subTodos: string[], priority?: 'high' | 'medium' | 'low') => {
    handleAddTodo(text, date, reminder, subTodos, priority);
    setIsMobileComposerOpen(false);
  };

  const groupedTodos = useMemo(() => {
    const now = new Date();
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    const tomorrowStart = todayStart + 86400000;
    const nextWeekStart = tomorrowStart + 86400000;

    const groups = {
      overdue: [] as Todo[],
      today: [] as Todo[],
      tomorrow: [] as Todo[],
      upcoming: {} as Record<string, Todo[]>, // Changed to map of date string -> todos
      noDate: [] as Todo[],
      completed: [] as Todo[]
    };

    todos.forEach(todo => {
      if (todo.completed) {
        groups.completed.push(todo);
        return;
      }

      if (!todo.dueDate) {
        groups.noDate.push(todo);
        return;
      }

      if (todo.dueDate < todayStart) {
        groups.overdue.push(todo);
      } else if (todo.dueDate < tomorrowStart) {
        groups.today.push(todo);
      } else if (todo.dueDate < nextWeekStart) {
        groups.tomorrow.push(todo);
      } else {
        // Group by specific date for upcoming
        const dateStr = new Date(todo.dueDate).toLocaleDateString();
        if (!groups.upcoming[dateStr]) {
            groups.upcoming[dateStr] = [];
        }
        groups.upcoming[dateStr].push(todo);
      }
    });

    // Sort noDate group by creation time (assuming newer todos are at the end of the array from useTodos?)
    // Actually useTodos adds new todos to the START usually? Let's check.
    // Wait, useTodos likely uses array state. If we push to array, it's chronological.
    // User wants "newest first". If `todos` is chronological (oldest first), we need to reverse.
    // If `todos` is reverse-chronological (newest first), then it's already correct.
    // Let's assume standard push behavior means oldest first. So we reverse.
    groups.noDate.reverse(); 

    return groups;
  }, [todos]);

  const [quickAddDate, setQuickAddDate] = useState<Date | null>(null);
  const [filterDate, setFilterDate] = useState<Date | null>(null);

  const handleDateSelect = (date: Date) => {
    // If clicking same date, clear filter
    if (filterDate && date.toDateString() === filterDate.toDateString()) {
        setFilterDate(null);
        setQuickAddDate(null);
    } else {
        setFilterDate(date);
        setQuickAddDate(date);
    }
  };

  const filteredTodos = useMemo(() => {
    if (!filterDate) return null;
    const targetStr = filterDate.toDateString();
    return todos.filter(t => {
        if (!t.dueDate) return false;
        return new Date(t.dueDate).toDateString() === targetStr;
    });
  }, [todos, filterDate]);

  const firstGroupId = useMemo(() => {
    if (filterDate) return 'filter';
    if (groupedTodos.overdue.length > 0) return 'overdue';
    if (groupedTodos.today.length > 0) return 'today';
    if (groupedTodos.tomorrow.length > 0) return 'tomorrow';
    
    // Check upcoming dates
    const upcomingDates = Object.keys(groupedTodos.upcoming).sort((a, b) => new Date(a).getTime() - new Date(b).getTime());
    if (upcomingDates.length > 0) return upcomingDates[0];
    
    if (groupedTodos.noDate.length > 0) return 'noDate';
    
    return null;
  }, [groupedTodos, filterDate]);


  return (
    <div className="h-screen flex flex-col bg-slate-50 dark:bg-slate-900 overflow-hidden">
      {/* Fixed Header for Mobile */}
      <div className="lg:hidden bg-white dark:bg-slate-900 border-b border-slate-100 dark:border-white/5 p-3 flex-shrink-0 z-40">
        <div className="flex items-center justify-between mb-2">
            <h1 className="text-lg font-bold text-slate-800 dark:text-white flex items-center gap-2">
                <ListTodo className="text-blue-500" size={20} /> 待办管理
            </h1>
        </div>
        <TodoStats todos={todos} compact={true} />
      </div>

      <div className="container mx-auto px-2 sm:px-4 py-4 sm:py-6 max-w-7xl flex-1 flex flex-col lg:min-h-0 overflow-hidden">
        <div className="hidden lg:flex justify-between items-center mb-6 flex-shrink-0">
            <div>
                <h1 className="text-xl sm:text-2xl font-bold text-slate-800 dark:text-white flex items-center gap-2">
                <ListTodo className="text-blue-500" /> 待办管理
                </h1>
                <p className="text-slate-500 dark:text-slate-400 text-xs sm:text-sm mt-1">
                    高效管理您的日常任务与计划
                </p>
            </div>
        </div>

        <div className="flex-1 min-h-0 grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-8 pb-20 lg:pb-0 relative">
            {/* Left Column: Input & Lists */}
            <div className="lg:col-span-8 flex flex-col h-full min-h-0">
                {/* Desktop Input Form */}
                <div className="hidden lg:block">
                    <AddTodoForm onAdd={handleAddTodo} initialDate={quickAddDate} />
                </div>
                
                <div className="flex-1 overflow-y-auto pl-5 pr-3 sm:px-4 lg:pl-8 custom-scrollbar space-y-2 pb-20 lg:pb-20 mt-2 lg:mt-0">
                    <style>{`
                        .custom-scrollbar::-webkit-scrollbar {
                            width: 6px;
                        }
                        .custom-scrollbar::-webkit-scrollbar-track {
                            background: transparent;
                        }
                        .custom-scrollbar::-webkit-scrollbar-thumb {
                            background-color: #cbd5e1;
                            border-radius: 20px;
                        }
                        .dark .custom-scrollbar::-webkit-scrollbar-thumb {
                            background-color: #475569;
                        }
                        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
                            background-color: #94a3b8;
                        }
                    `}</style>
                    
                    {filterDate ? (
                        <div className="space-y-4">
                            <div className="flex items-center justify-between mb-2">
                                <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-200 flex items-center gap-2">
                                    <Calendar size={20} className="text-blue-500" />
                                    {filterDate.toLocaleDateString()} 的待办
                                    <span className="text-sm font-normal text-slate-400 bg-slate-100 dark:bg-slate-800 px-2 py-0.5 rounded-full">
                                        {filteredTodos?.length || 0}
                                    </span>
                                </h3>
                                <button 
                                    onClick={() => {
                                        setFilterDate(null);
                                        setQuickAddDate(null);
                                    }}
                                    className="text-sm text-slate-400 hover:text-slate-600 flex items-center gap-1"
                                >
                                    <X size={14} /> 清除筛选
                                </button>
                            </div>
                            {/* Filtered View for Heatmap Date Selection */}
                            {filteredTodos && filteredTodos.length > 0 ? (
                                <TimelineGroup 
                                    title={`${filterDate ? filterDate.toLocaleDateString() : 'Selected'} (筛选)`} 
                                    todos={filteredTodos} 
                                    colorClass="bg-orange-500" 
                                    icon={Calendar} 
                                    hideTitle={true} // Add prop to hide title
                                    id="filter"
                                    defaultCollapsed={false}
                                    onToggle={toggleTodo} 
                                    onDelete={deleteTodo}
                                    onAddSubTodo={addSubTodo}
                                    onToggleSubTodo={toggleSubTodo}
                                    onDeleteSubTodo={deleteSubTodo}
                                    onUpdateReminder={updateTodoReminder}
                                    onUpdateDueDate={updateTodoDueDate}
                                    onUpdateText={updateTodoText}
                                    onUpdateSubTodoText={updateSubTodoText}
                                    onUpdatePriority={updateTodoPriority}
                                />
                            ) : (
                                <div className="text-center py-10 text-slate-400">
                                    <p>该日期下暂无待办任务</p>
                                    <button 
                                        onClick={() => {
                                            // Focus input? Already set quickAddDate
                                        }}
                                        className="text-blue-500 hover:underline mt-2 text-sm"
                                    >
                                        在上方添加一个?
                                    </button>
                                </div>
                            )}
                        </div>
                    ) : (
                        <>
                            <TimelineGroup 
                                title="已逾期" 
                                todos={groupedTodos.overdue} 
                                colorClass="bg-red-500" 
                                icon={AlertCircle} 
                                id="overdue"
                                defaultCollapsed={firstGroupId !== 'overdue'}
                                onToggle={toggleTodo} 
                                onDelete={deleteTodo}
                                onAddSubTodo={addSubTodo}
                                onToggleSubTodo={toggleSubTodo}
                                onDeleteSubTodo={deleteSubTodo}
                                onUpdateReminder={updateTodoReminder}
                                onUpdateDueDate={updateTodoDueDate}
                                onUpdateText={updateTodoText}
                                onUpdateSubTodoText={updateSubTodoText}
                                onUpdatePriority={updateTodoPriority}
                            />

                            <TimelineGroup 
                                title="今天" 
                                todos={groupedTodos.today} 
                                colorClass="bg-blue-500" 
                                icon={Calendar} 
                                id="today"
                                defaultCollapsed={firstGroupId !== 'today'}
                                onToggle={toggleTodo} 
                                onDelete={deleteTodo}
                                onAddSubTodo={addSubTodo}
                                onToggleSubTodo={toggleSubTodo}
                                onDeleteSubTodo={deleteSubTodo}
                                onUpdateReminder={updateTodoReminder}
                                onUpdateDueDate={updateTodoDueDate}
                                onUpdateText={updateTodoText}
                                onUpdateSubTodoText={updateSubTodoText}
                                onUpdatePriority={updateTodoPriority}
                            />
                            
                            {groupedTodos.today.length === 0 && groupedTodos.overdue.length === 0 && (
                                <div className="pl-8 py-8 text-slate-400 italic">
                                    今天没有待办任务，享受美好的一天！
                                </div>
                            )}

                            <TimelineGroup 
                                title="明天" 
                                todos={groupedTodos.tomorrow} 
                                colorClass="bg-purple-500" 
                                icon={Calendar} 
                                id="tomorrow"
                                defaultCollapsed={firstGroupId !== 'tomorrow'}
                                onToggle={toggleTodo} 
                                onDelete={deleteTodo}
                                onAddSubTodo={addSubTodo}
                                onToggleSubTodo={toggleSubTodo}
                                onDeleteSubTodo={deleteSubTodo}
                                onUpdateReminder={updateTodoReminder}
                                onUpdateDueDate={updateTodoDueDate}
                                onUpdateText={updateTodoText}
                                onUpdateSubTodoText={updateSubTodoText}
                                onUpdatePriority={updateTodoPriority}
                            />

                            {Object.entries(groupedTodos.upcoming).sort(([dateA], [dateB]) => new Date(dateA).getTime() - new Date(dateB).getTime()).map(([dateStr, todos]) => (
                                <TimelineGroup 
                                    key={dateStr}
                                    title={dateStr} 
                                    todos={todos} 
                                    colorClass="bg-slate-400" 
                                    icon={Calendar} 
                                    id={dateStr}
                                    defaultCollapsed={firstGroupId !== dateStr}
                                    onToggle={toggleTodo} 
                                    onDelete={deleteTodo}
                                    onAddSubTodo={addSubTodo}
                                    onToggleSubTodo={toggleSubTodo}
                                    onDeleteSubTodo={deleteSubTodo}
                                    onUpdateReminder={updateTodoReminder}
                                    onUpdateDueDate={updateTodoDueDate}
                                    onUpdateText={updateTodoText}
                                    onUpdateSubTodoText={updateSubTodoText}
                                    onUpdatePriority={updateTodoPriority}
                                />
                            ))}

                            <TimelineGroup 
                                title="无日期" 
                                todos={groupedTodos.noDate} 
                                colorClass="bg-slate-300" 
                                icon={Calendar} 
                                id="noDate"
                                defaultCollapsed={firstGroupId !== 'noDate'}
                                onToggle={toggleTodo} 
                                onDelete={deleteTodo}
                                onAddSubTodo={addSubTodo}
                                onToggleSubTodo={toggleSubTodo}
                                onDeleteSubTodo={deleteSubTodo}
                                onUpdateReminder={updateTodoReminder}
                                onUpdateDueDate={updateTodoDueDate}
                                onUpdateText={updateTodoText}
                                onUpdateSubTodoText={updateSubTodoText}
                                onUpdatePriority={updateTodoPriority}
                            />

                            {groupedTodos.completed.length > 0 && (
                                <div className="pt-4 pl-0 md:pl-6 relative">
                                     <div className="hidden md:block absolute left-[-8px] top-0 bottom-0 w-px bg-slate-200 dark:bg-slate-700"></div>
                                     {/* Group Header Node for Completed */}
                                     <div className="hidden md:block absolute left-[-11px] top-5 w-2 h-2 rounded-full ring-2 ring-white dark:ring-slate-900 bg-green-500"></div>

                                     <CompletedGroup 
                                        todos={groupedTodos.completed} 
                                        onToggle={toggleTodo} 
                                        onDelete={deleteTodo}
                                        onAddSubTodo={addSubTodo}
                                        onToggleSubTodo={toggleSubTodo}
                                        onDeleteSubTodo={deleteSubTodo}
                                        onUpdateReminder={updateTodoReminder}
                                        onUpdateDueDate={updateTodoDueDate}
                                        onUpdateText={updateTodoText}
                                        onUpdateSubTodoText={updateSubTodoText}
                                        onUpdatePriority={updateTodoPriority}
                                     />
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>

            {/* Right Column: Calendar & Stats - Hidden on Mobile */}
            <div className="hidden lg:block lg:col-span-4 space-y-6 pb-10">
                <div className="bg-white dark:bg-slate-800 p-4 sm:p-6 rounded-2xl shadow-sm border border-slate-200 dark:border-white/5">
                    <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-200 mb-4 flex items-center gap-2">
                        <Activity size={16} /> 待办热力图
                    </h3>
                    <TodoHeatmap 
                        todos={todos} 
                        onDateSelect={handleDateSelect} 
                        selectedDate={filterDate}
                    />
                </div>

                <TodoStats todos={todos} />

                {(!settings?.feishuConfig?.webhookUrl) && (
                <div className="bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl p-6 text-white shadow-lg relative overflow-hidden">
                    <div className="relative z-10">
                        <h3 className="text-lg font-bold mb-1">飞书助手</h3>
                        <p className="text-blue-100 text-sm mb-4">开启每日提醒与统计推送</p>
                        <Link 
                            to="/settings" 
                            className="inline-flex items-center gap-2 px-3 py-1.5 bg-white/20 hover:bg-white/30 rounded-lg text-sm transition-colors backdrop-blur-sm border border-white/10"
                        >
                            <Bell size={14} /> 去配置
                        </Link>
                    </div>
                    <div className="absolute right-0 bottom-0 opacity-10 transform translate-x-4 translate-y-4">
                        <Bell size={120} />
                    </div>
                </div>
                )}
            </div>
        </div>

        {isMobileComposerOpen && (
            <>
                <div
                    className="lg:hidden fixed inset-0 bg-black/20 backdrop-blur-[1px] z-40"
                    onClick={() => setIsMobileComposerOpen(false)}
                />
                <div className="lg:hidden fixed left-0 right-0 bottom-20 z-50 px-3">
                    <div
                        className="bg-white/95 dark:bg-slate-900/95 backdrop-blur-xl rounded-2xl border border-slate-200/70 dark:border-white/10 shadow-2xl p-2"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <AddTodoForm onAdd={handleAddTodoFromMobile} initialDate={quickAddDate} />
                    </div>
                </div>
            </>
        )}

        <div className="lg:hidden fixed bottom-4 left-0 right-0 z-50 px-4 pointer-events-none">
            <div className="flex justify-center">
                <button
                    type="button"
                    onClick={() => setIsMobileComposerOpen(v => !v)}
                    className="pointer-events-auto inline-flex items-center gap-2 px-5 py-3 rounded-full bg-blue-600 text-white shadow-lg shadow-blue-500/30 border border-blue-500/40 active:scale-95 transition-all"
                >
                    {isMobileComposerOpen ? <X size={18} /> : <Plus size={18} />}
                    <span className="text-sm font-medium">{isMobileComposerOpen ? '收起入口' : '新建任务'}</span>
                </button>
            </div>
        </div>
      </div>
    </div>
  );
};

export default Todos;
